import { PropertyValueMap, css, html } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { BaseElement } from "./BaseElement";
import { designTokensCss } from "./designTokens";

const getCenterElement = (element: HTMLElement | DOMRect) => {
  let bound: DOMRect = new DOMRect();
  if (element instanceof HTMLElement) {
    bound = element.getBoundingClientRect();
  } else if (element instanceof DOMRect) {
    bound = element;
  }

  return { x: bound.x + bound.width * 0.5, y: bound.y + bound.height * 0.5 };
};

const getClientPosition = (e: MouseEvent | TouchEvent) => {
  if (e instanceof MouseEvent) {
    return { x: e.clientX, y: e.clientY };
  } else if (e instanceof TouchEvent) {
    return { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }
  return { x: 0, y: 0 };
};

const lerp = (a: number, b: number, t: number) => {
  if (a - b < 1.0) return b;
  t = t > 1 ? 1 : t < 0 ? 0 : t;
  return a + (b - a) * t;
};

export const rotatePaneCSS = css`
  ${designTokensCss}

  .rotate-pane-container {
    width: 86px;
    height: 86px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: #ffffff00;
    z-index: 10;
  }

  .rotate-pane-stick {
    width: 37px;
    height: 37px;
    background-color: #ffffffee;
    position: absolute;

    transform: translate(-50%, 50%);
    border-radius: 50%;

    align-items: center;
    display: flex;
    justify-content: center;

    cursor: grab;

    box-shadow: 0px 0px 2px 2px #e1dcdc;
  }

  .rotate-pane-stick-text {
    position: absolute;
    font-size: 12px;
    color: var(--srd-accent);
  }

  #progress {
    stroke: var(--srd-accent);
  }

  .rotate-pane-stick svg path {
    fill: var(--srd-accent);
  }
`;

@customElement("rotate-pane")
export class RotatePane extends BaseElement {
  static styles = rotatePaneCSS;

  @property({ type: Number, attribute: "snap-angle" })
  snapAngle = 5;

  @query("#progress")
  private _progress?: HTMLElement;
  private _progressValue = 0;
  private _progressTeta = 0;

  private _isDragging = false;
  private _dragStart = { x: 0, y: 0 };
  private _intervalFunc: any = undefined;
  private _target = { x: 43, y: 0 };
  private _current = { x: 43, y: 0 };

  private _offset = { x: 43, y: 0 };

  @query(".rotate-pane-stick")
  private _stick?: HTMLElement;

  @query(".rotate-pane-container")
  private _container?: HTMLElement;

  @query(".rotate-pane-stick-text")
  private _text?: HTMLElement;

  private _boundOnStickDown = (e: MouseEvent | TouchEvent) => this.onStickDown(e);
  private _boundOnClick = (e: MouseEvent | TouchEvent) => this.onClick(e);
  private _boundOnStickUp = (e: MouseEvent | TouchEvent) => this.onStickUp(e);
  private _boundOnOver = (e: Event) => this.onOver(e as MouseEvent | TouchEvent);

  firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>
  ): void {
    this.progressValue = 0;

    this._stick?.addEventListener("mousedown", this._boundOnStickDown);
    this._stick?.addEventListener("click", this._boundOnClick);
    window?.addEventListener("mouseup", this._boundOnStickUp);
    this._stick?.addEventListener("touchstart", this._boundOnStickDown);
    this._stick?.addEventListener("touchend", this._boundOnStickUp);

    window.addEventListener("mousemove", this._boundOnOver, true);
    window.addEventListener("touchmove", this._boundOnOver, true);

    this.progressTeta = 0;
    this._intervalFunc = setInterval(() => {
      this._current.x = lerp(this._current.x, this._target.x, 0.75);
      this._current.y = lerp(this._current.y, this._target.y, 0.75);

      this._stick!.style.left = this._current.x + "px";
      this._stick!.style.bottom = -this._current.y + "px";
    }, 25);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._stick?.removeEventListener("mousedown", this._boundOnStickDown);
    this._stick?.removeEventListener("click", this._boundOnClick);
    window.removeEventListener("mouseup", this._boundOnStickUp);
    this._stick?.removeEventListener("touchstart", this._boundOnStickDown);
    this._stick?.removeEventListener("touchend", this._boundOnStickUp);
    window.removeEventListener("mousemove", this._boundOnOver, true);
    window.removeEventListener("touchmove", this._boundOnOver, true);
    if (this._intervalFunc !== undefined) {
      clearInterval(this._intervalFunc);
      this._intervalFunc = undefined;
    }
  }

  onStickDown(e: MouseEvent | TouchEvent) {
    e.preventDefault();
    this._isDragging = true;
    this._offset.x = this._container!.offsetWidth * 0.5;
    this._dragStart = getClientPosition(e);
  }

  onClick(e: MouseEvent | TouchEvent) {
    e.preventDefault();
  }

  onStickUp(e: MouseEvent | TouchEvent) {
    this._isDragging = false;
    this.progressValue = 0;
    this._target.x = this._offset.x;
    this._target.y = this._offset.y;
    this._text!.innerHTML = "0";
    this.dispatchEvent(new CustomEvent("end", { detail: this }));
  }

  onOver(e: MouseEvent | TouchEvent) {
    if (this._isDragging) {
      e.preventDefault();
      e.stopPropagation();
      const { x, y } = getClientPosition(e);
      this._target.x = this._offset.x + x - this._dragStart.x;
      this._target.y = this._offset.y + y - this._dragStart.y;

      const center = getCenterElement(this._container!);
      const xx = x - center.x;
      const yy = y - center.y;
      let teta = Math.atan2(-xx, yy);
      teta = teta < 0 ? teta + 2 * Math.PI : teta;

      const step = this.snapAngle;
      if (step > 0) {
        const deg = (teta * 180) / Math.PI;
        const snappedDeg = Math.round(deg / step) * step;
        teta = (snappedDeg * Math.PI) / 180;
      }

      this.progressTeta = teta;
    }
  }

  set progressTeta(value: number) {
    this._progressTeta = value;
    const radius = this._progress!.getAttribute("r")!;
    const progressValue = parseFloat(radius) * value;
    this.progressValue = progressValue;
    this._text!.innerHTML = ((value * 180) / Math.PI).toFixed(0);
    this.dispatchEvent(new CustomEvent("change", { detail: this }));
  }

  get progressTeta() {
    return this._progressTeta;
  }

  set progressValue(value: number) {
    this._progressValue = value;
    if (this._progress) {
      const radius = this._progress.getAttribute("r")!;
      const circumference = parseFloat(radius) * 2 * Math.PI;
      this._progress.setAttribute("stroke-dasharray", [
        this._progressValue,
        circumference - this._progressValue,
      ] as any);

      this._progress.setAttribute(
        "stroke-dashoffset",
        -(0.25 * circumference) as any
      );
    }
  }

  get progressValue() {
    return this._progressValue;
  }

  protected renderHtml() {
    return html /*html*/`
      <div class="rotate-pane-container">
        <svg
          style="width:100%;height:100%"
          viewBox="0 0 100 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="50"
            cy="50"
            r="48"
            stroke="white"
            stroke-opacity="0.8"
            stroke-width="4"
          />

          <circle
            id="progress"
            cx="50"
            cy="50"
            r="48"
            stroke-opacity="1.0"
            stroke-width="4"
          />
        </svg>
        <div class="rotate-pane-stick">
          <svg
            style="width:25px;height:25px"
            viewBox="0 0 25 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M25 12.5C25 13.6393 24.8494 14.7461 24.5483 15.8203C24.2472 16.8945 23.82 17.9036 23.2666 18.8477C22.7132 19.7917 22.0337 20.6584 21.228 21.4478C20.4224 22.2371 19.5231 22.9004 18.5303 23.4375H21.875V25H15.625V18.75H17.1875V22.3755C18.1396 21.9279 18.9982 21.3582 19.7632 20.6665C20.5282 19.9748 21.1873 19.2017 21.7407 18.3472C22.2941 17.4927 22.7132 16.5649 22.998 15.564C23.2829 14.563 23.4294 13.5417 23.4375 12.5C23.4375 11.499 23.3073 10.5347 23.0469 9.60693C22.7865 8.6792 22.4202 7.80843 21.9482 6.99463C21.4762 6.18083 20.9025 5.44027 20.2271 4.77295C19.5516 4.10563 18.8151 3.53597 18.0176 3.06396C17.2201 2.59196 16.3493 2.22168 15.4053 1.95312C14.4613 1.68457 13.4928 1.55436 12.5 1.5625C11.499 1.5625 10.5347 1.69271 9.60693 1.95312C8.6792 2.21354 7.80843 2.57975 6.99463 3.05176C6.18083 3.52376 5.44027 4.09749 4.77295 4.77295C4.10563 5.4484 3.53597 6.1849 3.06396 6.98242C2.59196 7.77995 2.22168 8.65072 1.95312 9.59473C1.68457 10.5387 1.55436 11.5072 1.5625 12.5H0C0 11.3525 0.146484 10.2458 0.439453 9.17969C0.732422 8.11361 1.15153 7.1167 1.69678 6.18896C2.24202 5.26123 2.89307 4.41895 3.6499 3.66211C4.40674 2.90527 5.25309 2.25423 6.18896 1.70898C7.12484 1.16374 8.11768 0.744629 9.16748 0.45166C10.2173 0.158691 11.3281 0.00813802 12.5 0C13.6475 0 14.7542 0.146484 15.8203 0.439453C16.8864 0.732422 17.8833 1.15153 18.811 1.69678C19.7388 2.24202 20.5811 2.89307 21.3379 3.6499C22.0947 4.40674 22.7458 5.25309 23.291 6.18896C23.8363 7.12484 24.2554 8.12175 24.5483 9.17969C24.8413 10.2376 24.9919 11.3444 25 12.5Z"
            />
          </svg>
          <div class="rotate-pane-stick-text" style="position:absolute">0</div>
        </div>
      </div>
    `;
  }
}
